import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import Portal from './Portal'

const GAP = 6
const VIEWPORT_MARGIN = 8
const OVERLAY_SELECTOR = '.ui-modal-root, .ui-drawer'

/** Menus portaled to body must paint above the dialog that opened them. */
const overlayZIndex = (anchorNode) => {
  const overlay = anchorNode?.closest?.(OVERLAY_SELECTOR)
  if (!overlay) return null
  const numeric = Number(window.getComputedStyle(overlay).zIndex)
  return Number.isFinite(numeric) ? numeric + 20 : null
}

/** antd placement names -> the geometry used to position the popup. */
const resolvePosition = (placement, anchor, popup, matchAnchorWidth) => {
  const width = matchAnchorWidth ? anchor.width : popup.width
  const [side, align = 'center'] = placement.split(/(?=[A-Z])/)
  const isVertical = side === 'top' || side === 'bottom'

  let top
  let left

  if (isVertical) {
    top = side === 'bottom' ? anchor.bottom + GAP : anchor.top - popup.height - GAP
    if (align === 'Left') left = anchor.left
    else if (align === 'Right') left = anchor.right - width
    else left = anchor.left + anchor.width / 2 - width / 2
  } else {
    left = side === 'right' ? anchor.right + GAP : anchor.left - width - GAP
    if (align === 'Top') top = anchor.top
    else if (align === 'Bottom') top = anchor.bottom - popup.height
    else top = anchor.top + anchor.height / 2 - popup.height / 2
  }

  return { top, left, width, side, isVertical }
}

/**
 * Anchored floating layer: positions `children` against `anchorRef`, flips when it
 * would overflow the viewport, and repositions on scroll and resize.
 *
 * @param {object} props
 * @param {React.RefObject} props.anchorRef element to position against
 * @param {boolean} props.open
 * @param {string} [props.placement] antd placement name, e.g. bottomLeft, topRight, right
 * @param {boolean} [props.matchAnchorWidth] force the popup to the anchor's width (Select)
 */
const Popup = ({
  anchorRef,
  open,
  placement = 'bottomLeft',
  matchAnchorWidth = false,
  zIndex,
  className = '',
  style,
  role,
  id,
  onMouseEnter,
  onMouseLeave,
  popupRef,
  children,
}) => {
  // The node lives in state, not just a ref: the portal host mounts on an effect,
  // so a ref alone would still be null on the first layout pass and the popup
  // would never get measured (and would stay hidden).
  const [node, setNode] = useState(null)
  const internalRef = useRef(null)
  const [position, setPosition] = useState(null)
  const [overlayZ, setOverlayZ] = useState(null)

  const attach = useCallback(
    (element) => {
      internalRef.current = element
      if (popupRef) popupRef.current = element
      setNode(element)
    },
    [popupRef]
  )

  const reposition = useCallback(() => {
    const anchorNode = anchorRef?.current
    const popupNode = internalRef.current
    if (!anchorNode || !popupNode) return

    const anchor = anchorNode.getBoundingClientRect()
    const popup = popupNode.getBoundingClientRect()

    let resolved = resolvePosition(placement, anchor, popup, matchAnchorWidth)

    // Flip to the opposite side when the preferred one does not fit.
    const overflowsBottom = resolved.top + popup.height > window.innerHeight - VIEWPORT_MARGIN
    const overflowsTop = resolved.top < VIEWPORT_MARGIN
    if (resolved.isVertical && (overflowsBottom || overflowsTop)) {
      const flipped = placement.startsWith('bottom')
        ? placement.replace('bottom', 'top')
        : placement.replace('top', 'bottom')
      const candidate = resolvePosition(flipped, anchor, popup, matchAnchorWidth)
      if (candidate.top >= VIEWPORT_MARGIN && candidate.top + popup.height <= window.innerHeight - VIEWPORT_MARGIN) {
        resolved = candidate
      }
    }

    const maxLeft = window.innerWidth - resolved.width - VIEWPORT_MARGIN
    const left = Math.max(VIEWPORT_MARGIN, Math.min(resolved.left, Math.max(VIEWPORT_MARGIN, maxLeft)))
    const maxTop = window.innerHeight - popup.height - VIEWPORT_MARGIN
    const top = Math.max(VIEWPORT_MARGIN, Math.min(resolved.top, Math.max(VIEWPORT_MARGIN, maxTop)))

    setPosition({ top, left, width: matchAnchorWidth ? resolved.width : undefined })
  }, [anchorRef, matchAnchorWidth, placement])

  useLayoutEffect(() => {
    if (!open || !node) {
      setPosition(null)
      setOverlayZ(null)
      return
    }
    setOverlayZ(overlayZIndex(anchorRef?.current))
    reposition()
  }, [anchorRef, node, open, reposition])

  useEffect(() => {
    if (!open) return undefined
    const onViewportChange = () => reposition()
    window.addEventListener('scroll', onViewportChange, true)
    window.addEventListener('resize', onViewportChange)
    return () => {
      window.removeEventListener('scroll', onViewportChange, true)
      window.removeEventListener('resize', onViewportChange)
    }
  }, [open, reposition])

  if (!open) return null

  return (
    <Portal>
      <div
        ref={attach}
        id={id}
        role={role}
        className={className}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={{
          position: 'fixed',
          top: position?.top ?? 0,
          left: position?.left ?? 0,
          width: position?.width,
          zIndex: zIndex ?? overlayZ ?? 'var(--z-popup, 1200)',
          // Hidden until measured, so it never flashes at the wrong spot.
          visibility: position ? 'visible' : 'hidden',
          ...style,
        }}
      >
        {children}
      </div>
    </Portal>
  )
}

export default Popup
